// Геометрия полотна и вычисления режимов: концы связей, кривые, степень узла,
// единственная точка отказа (шарнир графа), «вписать».

import type { FlowEdge, FlowNode, FlowStack, Scheme } from './types'

export const CARD_W = 248
export const STACK_W = 248
export const STACK_H = 44
/** строка названия над карточкой (снаружи) */
export const TITLE_H = 64
/** On the target card the title plate covers only the left part of its top, leaving room for the incoming arrow. */
const PLATE_SHARE = 0.7
export const K_MIN = 0.25
export const K_MAX = 2.5

export const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v))

export interface Box { x: number; y: number; w: number; h: number }

export function nodeBox(n: FlowNode, heights: Record<string, number>): Box {
  return { x: n.x, y: n.y, w: n.w || CARD_W, h: heights[n.id] ?? 120 }
}

export function stackBox(s: FlowStack): Box {
  return { x: s.x, y: s.y, w: STACK_W, h: STACK_H }
}

/** Прямоугольник конца связи: узел на полотне или стопка. */
export function endBox(scheme: Scheme, id: string, heights: Record<string, number>): Box | null {
  const st = scheme.stacks.find((s) => s.id === id)
  if (st) return stackBox(st)
  const n = scheme.nodes.find((x) => x.id === id)
  if (!n || n.stack) return null
  return nodeBox(n, heights)
}

type Pt = [number, number]

function bez(a: Pt, b: Pt): string {
  const dx = Math.max(40, Math.abs(b[0] - a[0]) / 2)
  return `M${a[0]} ${a[1]} C${a[0] + dx} ${a[1]}, ${b[0] - dx} ${b[1]}, ${b[0]} ${b[1]}`
}

/** Кривая связи: справа налево по горизонтали, сверху вниз, если блоки друг над другом. */
export function edgePath(A: Box, B: Box, kind?: string | null): string {
  const Ar: Pt = [A.x + A.w, A.y + A.h / 2]
  const Bl: Pt = [B.x, B.y + B.h / 2]
  if (kind === 'nest') return bez([Ar[0] + 4, Ar[1]], [Bl[0] - 1, Bl[1] + 14])
  const Al: Pt = [A.x, A.y + A.h / 2]
  const Br: Pt = [B.x + B.w, B.y + B.h / 2]
  const overlapX = A.x < B.x + B.w && B.x < A.x + A.w
  if (overlapX) {
    const down = A.y + A.h < B.y
    const a: Pt = down ? [A.x + A.w / 2, A.y + A.h] : [A.x + A.w / 2, A.y - TITLE_H]
    const b: Pt = down ? [B.x + B.w / 2, B.y - TITLE_H] : [B.x + B.w / 2, B.y + B.h]
    const my = (a[1] + b[1]) / 2
    return `M${a[0]} ${a[1]} C${a[0]} ${my}, ${b[0]} ${my}, ${b[0]} ${b[1]}`
  }
  if (Br[0] < Al[0]) {
    // цель слева — выходим слева, входим справа
    const dx = Math.max(40, (Al[0] - Br[0]) / 2)
    return `M${Al[0]} ${Al[1]} C${Al[0] - dx} ${Al[1]}, ${Br[0] + dx} ${Br[1]}, ${Br[0]} ${Br[1]}`
  }
  return bez(Ar, Bl)
}

export function degrees(edges: FlowEdge[]): Record<string, number> {
  const d: Record<string, number> = {}
  for (const e of edges) {
    d[e.from] = (d[e.from] ?? 0) + 1
    d[e.to] = (d[e.to] ?? 0) + 1
  }
  return d
}

/** Шарниры неориентированного графа (Тарьян): удаление узла рвёт связность —
 *  это и есть «единственная точка отказа» режима «элементы». */
export function articulation(ids: string[], edges: FlowEdge[]): Set<string> {
  const adj = new Map<string, string[]>()
  ids.forEach((id) => adj.set(id, []))
  for (const e of edges) {
    if (!adj.has(e.from) || !adj.has(e.to) || e.from === e.to) continue
    adj.get(e.from)!.push(e.to)
    adj.get(e.to)!.push(e.from)
  }
  const disc = new Map<string, number>()
  const low = new Map<string, number>()
  const out = new Set<string>()
  let t = 0
  const dfs = (u: string, parent: string | null) => {
    disc.set(u, ++t)
    low.set(u, t)
    let children = 0
    for (const v of adj.get(u)!) {
      if (!disc.has(v)) {
        children++
        dfs(v, u)
        low.set(u, Math.min(low.get(u)!, low.get(v)!))
        if (parent !== null && low.get(v)! >= disc.get(u)!) out.add(u)
      } else if (v !== parent) {
        low.set(u, Math.min(low.get(u)!, disc.get(v)!))
      }
    }
    if (parent === null && children > 1) out.add(u)
  }
  ids.forEach((id) => { if (!disc.has(id)) dfs(id, null) })
  return out
}

/** Рамка всей схемы (с учётом строки названия над карточками). */
export function bounds(scheme: Scheme, heights: Record<string, number>): Box | null {
  const boxes: Box[] = [
    ...(scheme.areas ?? []).map((a) => ({ x: Math.min(...a.points.map((p) => p[0])), y: Math.min(...a.points.map((p) => p[1])), w: Math.max(...a.points.map((p) => p[0])) - Math.min(...a.points.map((p) => p[0])), h: Math.max(...a.points.map((p) => p[1])) - Math.min(...a.points.map((p) => p[1])) })),
    ...scheme.nodes.filter((n) => !n.stack).map((n) => nodeBox(n, heights)),
    ...scheme.stacks.filter((st) => scheme.nodes.some((n) => n.stack === st.id)).map(stackBox),
  ]
  if (!boxes.length) return null
  const x0 = Math.min(...boxes.map((b) => b.x))
  const y0 = Math.min(...boxes.map((b) => b.y - TITLE_H))
  const x1 = Math.max(...boxes.map((b) => b.x + b.w))
  const y1 = Math.max(...boxes.map((b) => b.y + b.h))
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
}

/** «Вписать»: вся схема в экран; в узком окне масштаб не ниже 0.6, центр —
 *  кластер входа (первый оркестратор и его соседи). */
export function fitCamera(
  scheme: Scheme, heights: Record<string, number>, cw: number, ch: number,
): { k: number; tx: number; ty: number } {
  const b = bounds(scheme, heights)
  if (!b) return { k: 1, tx: cw / 2 - CARD_W / 2, ty: ch / 3 }
  const top = scheme.imported && !scheme.imported.dismissed ? 56 : 24
  const bottom = 110 // плавающая панель
  const raw = Math.min((cw - 64) / b.w, (ch - top - bottom) / b.h)
  const k = clamp(Math.min(raw, 1.1), K_MIN, K_MAX)
  if (raw >= 0.6) {
    return { k, tx: (cw - b.w * k) / 2 - b.x * k, ty: top + (ch - top - bottom - b.h * k) / 2 - b.y * k }
  }
  const k2 = 0.6
  const entry = scheme.nodes.find((n) => n.type === 'orchestrator' && !n.stack) ?? scheme.nodes.find((n) => !n.stack)
  if (!entry) return { k: k2, tx: 24 - b.x * k2, ty: top - b.y * k2 }
  const box = nodeBox(entry, heights)
  // кластер входа: оркестратор и его соседи по связям
  const near = new Set<string>([entry.id])
  scheme.edges.forEach((e) => { if (e.from === entry.id) near.add(e.to); if (e.to === entry.id) near.add(e.from) })
  const cl = [...near].map((id) => endBox(scheme, id, heights)).filter(Boolean) as Box[]
  const cx0 = Math.min(...cl.map((c) => c.x))
  const cx1 = Math.max(...cl.map((c) => c.x + c.w))
  const cx = cw >= (cx1 - cx0) * k2 + 32 ? (cx0 + cx1) / 2 : box.x + box.w / 2
  return { k: k2, tx: cw / 2 - cx * k2, ty: Math.max(top, (ch - bottom) / 2 - (box.y + box.h / 2) * k2) }
}

/** Свободное место для новой карточки: центр экрана, со сдвигом по спирали,
 *  если там уже лежит карточка или стопка (с учётом строки названия). */
export function freeSpot(
  scheme: Scheme, heights: Record<string, number>, cx: number, cy: number, h = 160,
): [number, number] {
  const pad = 28
  const boxes: Box[] = [
    ...scheme.nodes.filter((n) => !n.stack).map((n) => nodeBox(n, heights)),
    ...scheme.stacks.map(stackBox),
  ]
  const free = (x: number, y: number) => !boxes.some((b) =>
    x < b.x + b.w + pad && b.x < x + CARD_W + pad && y - TITLE_H < b.y + b.h + pad && b.y - TITLE_H < y + h + pad)
  const x0 = cx - CARD_W / 2
  const y0 = cy - h / 2
  if (free(x0, y0)) return [x0, y0]
  for (let r = 1; r < 40; r++) {
    const step = 60 * r
    for (let i = 0; i < 8 * r; i++) {
      const a = (i / (8 * r)) * Math.PI * 2
      const x = x0 + Math.cos(a) * step * 1.4
      const y = y0 + Math.sin(a) * step
      if (free(x, y)) return [Math.round(x), Math.round(y)]
    }
  }
  return [x0, y0]
}

export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10
  const m100 = n % 100
  if (m10 === 1 && m100 !== 11) return `${n} ${one}`
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return `${n} ${few}`
  return `${n} ${many}`
}

export function fmtRate(r: number): string {
  return r >= 1000 ? (r / 1000).toFixed(1).replace('.', ',') + 'k' : String(Math.round(r))
}


export function allowedEdge(a?: FlowNode, b?: FlowNode): boolean {
  return !!a && !!b && ((a.type === 'orchestrator' && b.type === 'subagent') ||
    (a.type === 'subagent' && ['mcp', 'storage', 'external'].includes(b.type)))
}

/** Explicit layout; keep manual positions until requested again. */
export function layeredLayout(scheme: Scheme, heights: Record<string, number> = {}): FlowNode[] {
  const nodes = scheme.nodes.map((n) => ({ ...n, stack: null }))
  const orchs = nodes.filter((n) => n.type === 'orchestrator')
  const agents = nodes.filter((n) => n.type === 'subagent')
  const sources = nodes.filter((n) => ['mcp', 'storage', 'external'].includes(n.type))
  orderLayers([orchs, agents, sources], scheme.edges)
  const height = (n: FlowNode) => heights[n.id] ?? Math.max(n.h, 160) + 80
  const columnW = Math.max(CARD_W, ...agents.map(n => n.w)) + 180
  const rowH = Math.max(120, ...agents.map(height)) + 220
  const span = Math.min(4, agents.length) * columnW - 180
  orchs.forEach((n, i) => { n.x = 40 + (Math.max(span, n.w) - n.w) / 2 + i * (n.w + 80); n.y = 60 })
  const top = Math.max(320, ...orchs.map(n => n.y + height(n) + 140))
  agents.forEach((n, i) => { n.x = 40 + (i % 4) * columnW; n.y = top + Math.floor(i / 4) * rowH })
  const ownersOf = (n: FlowNode) => agents.filter(a => scheme.edges.some(e => e.from === a.id && e.to === n.id))
  const barycentre = (n: FlowNode) => {
    const owners = ownersOf(n)
    return owners.length ? owners.reduce((sum, a) => sum + agents.indexOf(a), 0) / owners.length : Infinity
  }
  sources.sort((a, b) => barycentre(a) - barycentre(b))
  let bottom = top - 100
  sources.forEach(n => {
    const owners = agents.filter(a => scheme.edges.some(e => e.from === a.id && e.to === n.id))
    n.x = 40 + Math.max(span, CARD_W) + 220
    n.y = Math.max(bottom + 100, owners.length ? owners.reduce((sum, a) => sum + a.y, 0) / owners.length : top)
    bottom = n.y + height(n)
  })
  return nodes
}

/** Use exactly the same row geometry for newly inserted cards. */
export function layerSpot(scheme: Scheme, type: FlowNode['type'], heights: Record<string, number>): [number, number] {
  const added = { id: '__new__', type, x: 0, y: 0, w: CARD_W, h: 160 } as FlowNode
  const n = layeredLayout({ ...scheme, nodes: [...scheme.nodes, added] }, heights).find(n => n.id === added.id)!
  return [n.x, n.y]
}


/** Repeated barycentre sweeps, retaining the best crossing count. */
export function orderLayers(layers: FlowNode[][], edges: FlowEdge[]): void {
  const count = () => {
    let total = 0
    for (let l = 0; l < layers.length - 1; l++) {
      const a = new Map(layers[l].map((n, i) => [n.id, i]))
      const b = new Map(layers[l + 1].map((n, i) => [n.id, i]))
      const links = edges.filter((e) => a.has(e.from) && b.has(e.to))
      links.forEach((e, i) => links.slice(i + 1).forEach((f) => {
        if ((a.get(e.from)! - a.get(f.from)!) * (b.get(e.to)! - b.get(f.to)!) < 0) total++
      }))
    }
    return total
  }
  let best = layers.map((l) => [...l]), score = count()
  for (let pass = 0; pass < 12; pass++) {
    const reverse = pass % 2 === 1
    const indices = reverse ? [1, 0] : [1, 2]
    for (const i of indices) {
      const near = new Map(layers[i + (reverse ? 1 : -1)].map((n, j) => [n.id, j]))
      const bary = (n: FlowNode, fallback: number) => {
        const positions = edges.flatMap((e) => e.from === n.id && near.has(e.to) ? [near.get(e.to)!] : e.to === n.id && near.has(e.from) ? [near.get(e.from)!] : [])
        return positions.length ? positions.reduce((a, b) => a + b, 0) / positions.length : fallback
      }
      const ranks = new Map(layers[i].map((n, j) => [n.id, bary(n, j)]))
      layers[i].sort((a, b) => ranks.get(a.id)! - ranks.get(b.id)!)
    }
    const next = count()
    if (next < score) { score = next; best = layers.map((l) => [...l]) }
  }
  layers.forEach((l, i) => l.splice(0, l.length, ...best[i]))
}

export function insidePolygon(point: Pt, polygon: Pt[]): boolean {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i], b = polygon[j]
    if ((a[1] > point[1]) !== (b[1] > point[1]) && point[0] < (b[0] - a[0]) * (point[1] - a[1]) / (b[1] - a[1]) + a[0]) inside = !inside
  }
  return inside
}

export function areaPath(points: Pt[]): string {
  if (points.length < 3) return points.map((p, i) => `${i ? 'L' : 'M'}${p.join(' ')}`).join(' ')
  const toward = (a: Pt, b: Pt): Pt => {
    const distance = Math.hypot(b[0] - a[0], b[1] - a[1])
    const t = distance ? Math.min(12 / distance, 0.25) : 0
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]
  }
  const parts = points.map((p, i) => {
    const entry = toward(p, points[(i + points.length - 1) % points.length])
    const exit = toward(p, points[(i + 1) % points.length])
    return `${i ? 'L' : 'M'}${entry.join(' ')} Q${p.join(' ')} ${exit.join(' ')}`
  })
  return parts.join(' ') + ' Z'

}

/** Orthogonal visibility grid. No segment may enter any card (including titles).
 * Separate fan-out ports and clearance lanes prevent coincident parallel edges. */
export function routeEdge(A: Box, B: Box, obstacles: Box[], lane = 0, direction?: 'down' | 'right', wires: Box[] = [], targetOffset = 0, relaxed = false, forced = false, approach = 0): Pt[] {
  const offset = lane * 12
  const vertical = direction === 'down' || (direction !== 'right' && A.x < B.x + B.w && B.x < A.x + A.w)
  const forward = vertical ? B.y > A.y : B.x > A.x
  const startPort: Pt = vertical ? [A.x + 24 + offset % Math.max(24, A.w - 48), A.y + (forward ? A.h : 0)] : [A.x + (forward ? A.w : 0), A.y + A.h / 2 + offset % 36]
  // top-down edges land on the card's top edge at the right, clear of the title plate on the left
  const endPort: Pt = vertical ? [B.x + B.w - 22, B.y + (forward ? 0 : B.h)] : [B.x + (forward ? 0 : B.w), B.y + B.h / 2 + targetOffset]
  const pad = 16
  const start: Pt = vertical ? [startPort[0], startPort[1] + (forward ? pad : -pad)] : [startPort[0] + (forward ? pad : -pad), startPort[1]]
  // each incoming edge gets its own approach lane, so their last bends never share a line
  const reach = pad + approach * 14
  const end: Pt = vertical ? [endPort[0], endPort[1] - (forward ? pad : -pad)] : [endPort[0] - (forward ? reach : -reach), endPort[1]]
  const boxes = [...obstacles.flatMap(b => [{ x: b.x - 16, y: b.y - 16, w: b.w + 32, h: b.h + 32 }, { x: b.x - 16, y: b.y - TITLE_H - 16, w: (b.x === B.x && b.y === B.y ? b.w * PLATE_SHARE : b.w) + 32, h: TITLE_H + 32 }]), ...(relaxed ? [] : wires)]
  // forced fallback searches a coarse grid (cards only) so it stays cheap
  const gridBoxes = relaxed && !forced ? [...boxes, ...wires] : boxes
  const xs = [...new Set([start[0], end[0], ...gridBoxes.flatMap((b) => [b.x, b.x + b.w, b.x - 16, b.x + b.w + 16])])].sort((a, b) => a - b)
  const ys = [...new Set([start[1], end[1], ...gridBoxes.flatMap((b) => [b.y, b.y + b.h, b.y - 16, b.y + b.h + 16])])].sort((a, b) => a - b)
  const clear = (a: Pt, b: Pt) => !boxes.some((r) => a[0] === b[0]
    ? a[0] > r.x && a[0] < r.x + r.w && Math.max(a[1], b[1]) > r.y && Math.min(a[1], b[1]) < r.y + r.h
    : a[1] > r.y && a[1] < r.y + r.h && Math.max(a[0], b[0]) > r.x && Math.min(a[0], b[0]) < r.x + r.w)
  const index = (p: Pt) => ys.indexOf(p[1]) * xs.length + xs.indexOf(p[0])
  const point = (i: number): Pt => [xs[i % xs.length], ys[Math.floor(i / xs.length)]]
  const first = index(start), last = index(end)
  const dist = new Map([[first, 0]]), prev = new Map<number, number>()
  const heap: { id: number; score: number; cost: number }[] = []
  const push = (id: number, cost: number) => {
    const p = point(id), entry = { id, cost, score: cost + Math.abs(p[0] - end[0]) + Math.abs(p[1] - end[1]) }
    let i = heap.length
    heap.push(entry)
    while (i > 0) {
      const parent = (i - 1) >> 1
      if (heap[parent].score <= entry.score) break
      heap[i] = heap[parent]; i = parent
    }
    heap[i] = entry
  }
  const pop = () => {
    const entry = heap[0], tail = heap.pop()!
    if (heap.length) {
      let i = 0
      while (2 * i + 1 < heap.length) {
        let child = 2 * i + 1
        if (child + 1 < heap.length && heap[child + 1].score < heap[child].score) child++
        if (heap[child].score >= tail.score) break
        heap[i] = heap[child]; i = child
      }
      heap[i] = tail
    }
    return entry
  }
  push(first, 0)
  // hard cap on explored states: a pathological grid falls through to the next, cheaper mode
  let budget = 12000
  while (heap.length && budget-- > 0) {
    const entry = pop()
    let u = entry.id
    if (entry.cost !== dist.get(u)) continue
    if (u === last) {
      const path = [end]; while (u !== first) { u = prev.get(u)!; path.unshift(point(u)) } return [startPort, ...straighten(path, [...boxes, ...wires]), endPort].filter((p, i, all) => {
        if (i === 0 || i === all.length - 1) return true
        const a = all[i - 1], b = all[i + 1]
        return !(a[0] === p[0] && p[0] === b[0] || a[1] === p[1] && p[1] === b[1])
      })
    }
    const x = u % xs.length, y = Math.floor(u / xs.length), a = point(u)
    for (const v of [x > 0 ? u - 1 : -1, x + 1 < xs.length ? u + 1 : -1, y > 0 ? u - xs.length : -1, y + 1 < ys.length ? u + xs.length : -1]) {
      if (v < 0) continue
      const b = point(v)
      const blocked = !clear(a, b)
      if (blocked && !forced) continue
      const penalty = (blocked ? 1000000 : 0) + (relaxed ? wires.reduce((sum, r) => sum + ((a[0] === b[0]
        ? a[0] > r.x && a[0] < r.x + r.w && Math.max(a[1], b[1]) > r.y && Math.min(a[1], b[1]) < r.y + r.h
        : a[1] > r.y && a[1] < r.y + r.h && Math.max(a[0], b[0]) > r.x && Math.min(a[0], b[0]) < r.x + r.w) ? 100000 : 0), 0) : 0)
      const cost = dist.get(u)! + Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + penalty
      if (cost < (dist.get(v) ?? Infinity)) { dist.set(v, cost); prev.set(v, u); push(v, cost) }
    }
  }
  if (!relaxed) return routeEdge(A, B, obstacles, lane, direction, wires, targetOffset, true, false, approach)
  // No clean route: cross a card rather than hide the edge (heavy penalty keeps it rare).
  if (!forced) return routeEdge(A, B, obstacles, lane, direction, wires, targetOffset, true, true, approach)
  return [startPort, start, [end[0], start[1]], end, endPort]
}
/** Replace stair-steps with the fewest straight or L-shaped legs that stay clear of every box
 * (cards and already routed wires), so tidying never adds a crossing. Ends stay in place. */
function straighten(path: Pt[], blockers: Box[]): Pt[] {
  const free = (a: Pt, b: Pt) => !blockers.some((r) => a[0] === b[0]
    ? a[0] > r.x && a[0] < r.x + r.w && Math.max(a[1], b[1]) > r.y && Math.min(a[1], b[1]) < r.y + r.h
    : a[1] > r.y && a[1] < r.y + r.h && Math.max(a[0], b[0]) > r.x && Math.min(a[0], b[0]) < r.x + r.w)
  const out: Pt[] = [path[0]]
  let i = 0
  while (i < path.length - 1) {
    let step: Pt[] = [path[i + 1]], next = i + 1
    for (let j = path.length - 1; j > i + 1; j--) {
      const a = path[i], b = path[j]
      if (a[0] === b[0] || a[1] === b[1]) { if (free(a, b)) { step = [b]; next = j; break } continue }
      const corner = ([[a[0], b[1]], [b[0], a[1]]] as Pt[]).find((c) => free(a, c) && free(c, b))
      if (corner) { step = [corner, b]; next = j; break }
    }
    out.push(...step); i = next
  }
  return out
}
export const polylinePath = (points: Pt[]) => points.map((p, i) => `${i ? 'L' : 'M'}${p.join(' ')}`).join(' ')

/** Route the whole graph so already allocated lanes remain at least 12px apart. */
export function routeGraph(scheme: Scheme, heights: Record<string, number> = {}, budgetMs = 80): Record<string, Pt[]> {
  // Full routing (lanes kept apart from earlier wires) is costly on big schemes; once the time
  // budget is spent the remaining edges only avoid cards. budgetMs = 0 → cards-only (used while dragging).
  const deadline = Date.now() + budgetMs
  const boxes = scheme.nodes.filter(n => !n.stack).map(n => nodeBox(n, heights))
  const wires: Box[] = [], result: Record<string, Pt[]> = {}
  const sorted = scheme.edges.filter(e => endBox(scheme, e.from, heights) && endBox(scheme, e.to, heights) && scheme.nodes.some(n => n.id === e.from) && scheme.nodes.some(n => n.id === e.to)).sort((a, b) => {
    const aa = scheme.nodes.find(n => n.id === a.from)!, ba = scheme.nodes.find(n => n.id === b.from)!
    const ab = scheme.nodes.find(n => n.id === a.to)!, bb = scheme.nodes.find(n => n.id === b.to)!
    return Number(ba?.type === 'orchestrator') - Number(aa?.type === 'orchestrator') || (aa?.type === 'orchestrator' ? bb.y - ab.y || Math.abs(bb.x - ba.x) - Math.abs(ab.x - aa.x) : ba.x - aa.x)
  })
  sorted.forEach((e) => {
    const a = endBox(scheme, e.from, heights), b = endBox(scheme, e.to, heights)
    if (!a || !b) return
    const down = scheme.nodes.find(n => n.id === e.from)?.type === 'orchestrator'
    const siblings = sorted.filter(f => f.from === e.from).sort((f, g) => { const F = endBox(scheme, f.to, heights)!, G = endBox(scheme, g.to, heights)!; return F.x - G.x || G.y - F.y })
    const lane = siblings.findIndex(f => f.id === e.id)
    const incoming = sorted.filter(f => f.to === e.to).sort((f, g) => {
      const F = endBox(scheme, f.from, heights)!, G = endBox(scheme, g.from, heights)!
      // nearest source takes the upper port, farther ones (routed around the row) come in below
      const dist = (X: Box) => Math.abs(X.x + X.w / 2 - (b!.x + b!.w / 2))
      return F.y - G.y || dist(F) - dist(G) || f.id.localeCompare(g.id)
    })
    const rank = incoming.findIndex(f => f.id === e.id)
    const targetOffset = (rank - (incoming.length - 1) / 2) * 12
    // upper ports bend farther out, lower ones closer to the card — their last legs don't cross
    const approach = down ? 0 : incoming.length - 1 - rank
    const full = Date.now() < deadline
    const path = routeEdge(a, b, boxes, lane, down ? 'down' : 'right', full ? wires : [], targetOffset, false, false, approach)
    result[e.id] = path
    path.slice(1).forEach((q, j) => {
      const p = path[j]
      wires.push({ x: Math.min(p[0], q[0]) - 12, y: Math.min(p[1], q[1]) - 12, w: Math.abs(p[0] - q[0]) + 24, h: Math.abs(p[1] - q[1]) + 24 })
    })
  })
  return result
}
